import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Grid, useMediaQuery } from '@material-ui/core';
import { useMeasure } from 'react-use';

// CONSTANTS
import { DisplayType, ServiceButtonType } from '@/constants/watchList';
import { ROUTE_DISCOVER_APP } from '@/constants/routes';

// UTILITY
import { updateUrl, popupCenter } from '@/utility/common';
import { AppPurchaseTypeVariant } from '@/utility/split';
import { getPlaceholderUrl } from '@/utility/content';
import getLink from '@/utility/routes';
import breakpoints from '@/utility/breakpoints';

// COMPONENTS
import Link from '@/components/Link';

// STYLES
import { AppsListStyled } from '../styles';

function getApps(apps = [], count = 1) {
  const appsLength = apps.length;
  const moreThanNecessary = appsLength > count;

  const newApps = apps.slice(0, moreThanNecessary ? count - 1 : count);

  return moreThanNecessary
    ? [
        ...newApps,
        {
          rest_count: `+${apps.length - newApps.length}`,
        },
      ]
    : newApps;
}

function AppsList({
  page,
  data,
  displayType,
  externalUrlParams,
  handleExternalLink,
  handleWatchNowСlick,
  analyticsLocation,
  isAuth,
  className,
}) {
  const downXS = useMediaQuery(breakpoints.down('xs'));
  const [t] = useTranslation();
  const [ref, { width }] = useMeasure();

  // const data = Array.from({ length: 10 }).fill(..._data);

  const isDefault = displayType === DisplayType.DEFAULT;
  const isDialog = displayType === DisplayType.DIALOG;
  const isTooltip = displayType === DisplayType.TOOLTIP;
  const isWatchlistPage = page === 'watchlist';
  const cellWidth = isDialog ? 80 : 45;
  const cellPadding = isDialog ? 8 : 4;
  const countItemsInLine = Math.floor((width + cellPadding) / (cellWidth + cellPadding));
  const appsCount = isDialog ? countItemsInLine * 2 : countItemsInLine;
  const apps = getApps(data, appsCount);

  function handleLinkClick(e) {
    if (isDefault && !downXS) {
      e.preventDefault();

      return;
    }

    if (isTooltip) {
      e.stopPropagation();
    }
  }

  function handleExtLinkClick(e, app, idx) {
    const { id, content_url, button_type } = app;
    const isWatchNow = button_type === ServiceButtonType.WATCH_NOW;

    if (isDefault && !downXS && !isWatchlistPage) {
      e.preventDefault();

      return;
    }

    if (isWatchNow || isTooltip) {
      e.stopPropagation();
    }

    if (isWatchNow) {
      e.preventDefault();
      popupCenter(
        updateUrl(content_url, externalUrlParams),
        window.innerWidth * 0.8,
        window.innerHeight * 0.8,
      );

      if (isAuth) handleWatchNowСlick();
    }

    handleExternalLink(e, {
      app_id: id,
      slot: idx + 1,
      type: isWatchNow ? 'content link' : 'app link',
    });
  }

  return (
    <AppsListStyled ref={ref} className={clsx(`apps--${displayType}`, className)}>
      <Typography className={clsx('apps-title', isDialog ? 'p4' : 'p5')} component="h6">
        {t(`common.${downXS && !isDefault ? 'watchNow' : 'availableOn'}`)}:
      </Typography>
      <div className="apps-list">
        <Grid container spacing={1}>
          {apps.map((app = {}, idx) => {
            const { name, thumbnail, unique_slug, rest_count, content_url } = app;

            const bgImage = `url(${thumbnail || getPlaceholderUrl({ title: name })})`;
            const hasExtLink = Boolean(content_url);
            const variant = AppPurchaseTypeVariant.getVariant(app);
            const appPurchaseTypeRentPurchase = AppPurchaseTypeVariant.isRentPurchase(variant);

            const elProps = hasExtLink
              ? [
                  'a',
                  {
                    href: updateUrl(content_url, externalUrlParams),
                    rel: 'noreferrer',
                    target: '_blank',
                    onClick: e => handleExtLinkClick(e, app, idx),
                  },
                ]
              : [
                  Link,
                  {
                    to: getLink(ROUTE_DISCOVER_APP, { slug: unique_slug }),
                    location: analyticsLocation,
                    onClick: handleLinkClick,
                  },
                ];

            return (
              <Grid item key={idx}>
                {rest_count ? (
                  <div className="app-card">
                    <Typography variant="h6" component="p" color="textPrimary">
                      {rest_count}
                    </Typography>
                  </div>
                ) : (
                  React.createElement(
                    elProps[0],
                    elProps[1],
                    <div
                      className={clsx(
                        'app-card',
                        appPurchaseTypeRentPurchase && 'badge-dollar badge-dollar--small',
                      )}
                      style={{ backgroundImage: bgImage }}
                    />,
                  )
                )}
              </Grid>
            );
          })}
        </Grid>
      </div>
    </AppsListStyled>
  );
}

AppsList.defaultProps = {
  isAuth: false,
  displayType: DisplayType.DEFAULT,
  handleWatchNowСlick: () => {},
  handleExternalLink: () => {},
  externalUrlParams: {},
  data: [],
  className: '',
};

AppsList.propTypes = {
  page: PropTypes.string,
  className: PropTypes.string,
  displayType: PropTypes.string,
  analyticsLocation: PropTypes.string,
  isAuth: PropTypes.bool,
  data: PropTypes.array,
  handleExternalLink: PropTypes.func,
  handleWatchNowСlick: PropTypes.func,
  externalUrlParams: PropTypes.object,
};

export default React.memo(AppsList);
